import { motion } from 'framer-motion';

import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Picture } from '@/components/picture';
import { company } from '@/data/company';

const headlineLines = ['Bäume verstehen.', 'Verantwortungsvoll', 'handeln.'];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.15 * i, ease: [0.2, 0.7, 0.2, 1] as const },
  }),
};

export function HomeHero(): React.ReactNode {
  return (
    <section
      className="relative min-h-[92vh] overflow-hidden bg-bark text-paper sm:min-h-[88vh]"
      aria-label="Willkommen bei Baumpflege Straesser"
    >
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
        className="absolute inset-0"
      >
        <Picture
          src="/images/hero-climber.jpg"
          alt="Baumpfleger in Seilklettertechnik beim Arbeiten am Stamm"
          className="absolute inset-0 h-full w-full"
          imgClassName="h-full w-full object-cover object-[60%_center] sm:object-[70%_center]"
          loading="eager"
          fetchPriority="high"
          width={1344}
          height={1980}
        />
      </motion.div>

      {/* Mobile gradient: dark top fade, transparent middle (photo dominates),
          subtle dark bottom for button legibility */}
      <div
        className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(28,34,24,0.95)_0%,rgba(28,34,24,0.6)_28%,rgba(28,34,24,0.05)_55%,rgba(28,34,24,0.7)_100%)] sm:hidden"
        aria-hidden="true"
      />
      {/* Desktop horizontal gradient */}
      <div
        className="absolute inset-0 hidden bg-gradient-to-r from-bark via-bark/85 to-bark/20 sm:block"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex min-h-[92vh] max-w-6xl flex-col justify-between px-5 pt-20 pb-10 sm:min-h-[88vh] sm:justify-center sm:px-8 sm:pt-40 sm:pb-28 lg:pt-56 lg:pb-40">
        {/* Top: tagline + headline + description */}
        <div className="max-w-3xl">
          <motion.p
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeUp}
            className="mb-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-leaf sm:mb-6 sm:text-xs"
          >
            Baumpflege · Baumhausbau · Baumfällung
          </motion.p>

          <h1 className="mb-6 font-display text-[clamp(2.25rem,8vw,5.5rem)] font-semibold leading-[0.95] tracking-tight text-paper sm:mb-8 [text-shadow:0_2px_24px_rgba(28,34,24,0.7)]">
            {headlineLines.map((line, i) => (
              <motion.span
                key={line}
                initial="hidden"
                animate="visible"
                custom={i + 1}
                variants={fadeUp}
                className="block"
              >
                {line}
              </motion.span>
            ))}
          </h1>

          <motion.p
            initial="hidden"
            animate="visible"
            custom={4}
            variants={fadeUp}
            className="max-w-xl text-sm text-paper/85 sm:text-xl [text-shadow:0_1px_12px_rgba(28,34,24,0.7)]"
          >
            Professionelle Baumpflege per Seilklettertechnik – auch auf schwierigem Gelände, in
            Gärten und Hinterhöfen. Kostenlose Erstbegutachtung in {company.serviceArea}.
          </motion.p>
        </div>

        {/* Bottom: CTAs sit on the photo */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={5}
          variants={fadeUp}
          className="flex flex-col gap-3 sm:flex-row sm:mt-10"
        >
          <Button as="link" to="/kontakt" size="lg" variant="inverse">
            <Icon name="Mail" className="h-5 w-5" />
            Kostenloses Angebot
          </Button>
          <Button
            as="anchor"
            href={company.phone.href}
            size="lg"
            variant="ghost"
            className="border border-paper/40 bg-bark/30 text-paper backdrop-blur hover:bg-paper/10"
          >
            <Icon name="Phone" className="h-5 w-5" />
            Jetzt anrufen
          </Button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-paper/60 sm:block"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-[0.3em]">Scroll</span>
          <Icon name="ChevronDown" className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}
